/**
 * Avatar del Gourmetero.
 *
 * GU-AUTH-04 + ADR-004 §C8 (ratificado): "con cara" significa identidad
 * persistente y atribuible, no una fotografía del rostro. Un avatar de
 * galería curada cuenta como cara; un logo de marca, no — por eso la
 * galería es cerrada y no se acepta cualquier imagen como "default".
 */

import {
  AVATAR_ACCEPTED_TYPES,
  AVATAR_GALLERY_PATHS,
  AVATAR_MAX_BYTES,
  AVATAR_MAX_DIMENSION_PX,
  isGalleryAvatar as esDeGaleria,
} from '@gu/contracts';

/**
 * Galería curada. Ilustraciones de personas comiendo, no logos ni iconos
 * genéricos: el registro visual debe seguir siendo humano aunque el
 * usuario no quiera exponer su rostro.
 */
/**
 * Las rutas salen de `@gu/contracts`, no de aquí: el servidor valida
 * contra esa misma lista al guardar el perfil. Si esta galería tuviera
 * rutas propias, el día que discreparan la PWA ofrecería avatares que el
 * servidor rechaza. Aquí viven las etiquetas y el orden, que son de
 * presentación y solo importan en la UI.
 */
export const AVATAR_GALLERY = [
  { id: 'taco', src: AVATAR_GALLERY_PATHS[0], alt: 'Taco sonriente' },
  { id: 'montadito', src: AVATAR_GALLERY_PATHS[1], alt: 'Montadito sonriente' },
  { id: 'cafe', src: AVATAR_GALLERY_PATHS[2], alt: 'Taza de café sonriente' },
  { id: 'dona', src: AVATAR_GALLERY_PATHS[3], alt: 'Dona sonriente' },
  { id: 'hotdog', src: AVATAR_GALLERY_PATHS[4], alt: 'Hot dog sonriente' },
  { id: 'pizza', src: AVATAR_GALLERY_PATHS[5], alt: 'Rebanada de pizza sonriente' },
  { id: 'ramen', src: AVATAR_GALLERY_PATHS[6], alt: 'Tazón de ramen sonriente' },
  { id: 'sushi', src: AVATAR_GALLERY_PATHS[7], alt: 'Pieza de sushi sonriente' },
  { id: 'dumpling', src: AVATAR_GALLERY_PATHS[8], alt: 'Dumpling sonriente' },
  { id: 'ensalada', src: AVATAR_GALLERY_PATHS[9], alt: 'Tazón de ensalada sonriente' },
  { id: 'bistec', src: AVATAR_GALLERY_PATHS[10], alt: 'Bistec sonriente' },
  { id: 'galleta', src: AVATAR_GALLERY_PATHS[11], alt: 'Galleta sonriente' },
  { id: 'galleta2', src: AVATAR_GALLERY_PATHS[12], alt: 'Galleta con chispas sonriente' },
  { id: 'banana', src: AVATAR_GALLERY_PATHS[13], alt: 'Plátano sonriente' },
  { id: 'gu', src: AVATAR_GALLERY_PATHS[14], alt: 'La mascota de Gourmet Urbano' },
] as const;

export type GalleryAvatarId = (typeof AVATAR_GALLERY)[number]['id'];

export interface AvatarValidationError {
  code: 'too_large' | 'wrong_type';
  message: string;
}

/**
 * Valida un archivo antes de procesarlo. Los mensajes son accionables:
 * dicen qué pasó y qué hacer, nunca "archivo inválido".
 */
export function validateAvatarFile(file: {
  size: number;
  type: string;
}): AvatarValidationError | null {
  if (!(AVATAR_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
    return {
      code: 'wrong_type',
      message: 'Esa imagen no se puede usar. Sirve JPG, PNG o WebP.',
    };
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return {
      code: 'too_large',
      message: 'La foto pesa demasiado. Elige una de menos de 5 MB.',
    };
  }
  return null;
}

/**
 * Calcula el tamaño de salida preservando proporción, con el lado mayor
 * acotado a 512px. GU-AUTH-04 exige redimensionar EN EL CLIENTE: subir una
 * foto de 12 MP desde datos móviles para que el servidor la encoja es
 * quemarle el plan al usuario.
 *
 * Nunca agranda: una imagen ya pequeña se sube tal cual.
 */
export function fitWithinMax(
  width: number,
  height: number,
  max: number = AVATAR_MAX_DIMENSION_PX,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  if (width <= max && height <= max) return { width, height };

  const ratio = Math.min(max / width, max / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * ¿Esta URL corresponde a un avatar de la galería curada?
 *
 * Delega en los contratos —la misma función que usa el servidor— para que
 * PWA y API no puedan discrepar sobre qué cuenta como cara.
 *
 * La versión anterior aceptaba por `endsWith`, así que
 * `https://malicioso.example/avatars/taquero-01.webp` pasaba: un tercero
 * podía servir la imagen y contar las visitas de quien viera el perfil.
 * La comparación es exacta.
 */
export function isGalleryAvatar(url: string | null | undefined): boolean {
  if (!url) return false;
  return esDeGaleria(url);
}
